"use client";

import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { AgenticaLogo } from "@/components/branding/agentica-logo";
import { SymbolicaLogo } from "@/components/branding/symbolica-logo";
import { cn, isLoginEnabled } from "@/lib/utils";
import { PillButton } from "@/components/ui/pill-button";
import { env } from "@/env";

type ActivePage = "agentica" | "agentica-sdk" | "research" | "blog" | "careers";

interface SiteNavigationProps {
  isDark: boolean;
  activePage?: ActivePage;
  logoOnly?: boolean;
  logo?: "symbolica" | "agentica";
}

type NavLink =
  | { href: string; label: string; page: ActivePage; external?: false }
  | { href: string; label: string; page?: undefined; external: true };

const NAV_LINKS_LEFT: NavLink[] = [
  { href: "/agentica", label: "Agentica", page: "agentica" },
  { href: "/agentica-sdk", label: "SDK", page: "agentica-sdk" },
  { href: "https://docs.symbolica.ai", label: "Docs", external: true },
  { href: "/blog", label: "Blog", page: "blog" },
];

const NAV_LINKS_RIGHT: NavLink[] = [
  { href: "/research", label: "Research", page: "research" },
  { href: "/careers", label: "Careers", page: "careers" },
];

const NAV_LINKS: NavLink[] = [...NAV_LINKS_LEFT, ...NAV_LINKS_RIGHT];

function NavItem({
  link,
  activePage,
  isDark,
}: {
  link: NavLink;
  activePage?: ActivePage;
  isDark: boolean;
}) {
  const isActive = "page" in link && link.page === activePage;
  const activeColor = isDark ? "text-white" : "text-[#0a0a0a]";
  const hoverColor = isDark ? "hover:text-white" : "hover:text-[#0a0a0a]";

  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(hoverColor, "transition-colors duration-200")}
      >
        {link.label}
      </a>
    );
  }

  return (
    <Link
      href={link.href}
      className={cn(
        "transition-colors duration-200",
        isActive ? activeColor : hoverColor
      )}
    >
      {link.label}
    </Link>
  );
}

function MobileNavItem({
  link,
  activePage,
  isDark,
  onClose,
}: {
  link: NavLink;
  activePage?: ActivePage;
  isDark: boolean;
  onClose: () => void;
}) {
  const isActive = "page" in link && link.page === activePage;
  const baseClass =
    "block rounded-md px-3 py-2.5 text-[15px] font-light transition-colors";
  const colorClass = isDark
    ? isActive
      ? "text-white hover:bg-white/5"
      : "text-white/70 hover:bg-white/5 hover:text-white"
    : isActive
      ? "text-[#0a0a0a] hover:bg-black/5"
      : "text-[#0a0a0a]/70 hover:bg-black/5 hover:text-[#0a0a0a]";

  return (
    <li>
      {link.external ? (
        <a
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(baseClass, colorClass)}
          onClick={onClose}
        >
          {link.label}
        </a>
      ) : (
        <Link
          href={link.href}
          className={cn(baseClass, colorClass)}
          onClick={onClose}
        >
          {link.label}
        </Link>
      )}
    </li>
  );
}

export function SiteNavigation({
  isDark,
  activePage,
  logoOnly,
  logo = "symbolica",
}: SiteNavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [useMobileLayout, setUseMobileLayout] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navMeasureLogoRef = useRef<HTMLDivElement>(null);
  const navMeasureLinksRef = useRef<HTMLDivElement>(null);
  const navMeasureActionsRef = useRef<HTMLDivElement>(null);
  const signInButtonRef = useRef<HTMLDivElement>(null);
  const signInDropdownRef = useRef<HTMLDivElement>(null);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const loginEnabled = isLoginEnabled();

  const toggleSignIn = useCallback(() => {
    setSignInOpen((prev) => {
      if (!prev && signInButtonRef.current) {
        const rect = signInButtonRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + 8,
          left: rect.left + rect.width / 2,
        });
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        signInButtonRef.current &&
        !signInButtonRef.current.contains(target) &&
        signInDropdownRef.current &&
        !signInDropdownRef.current.contains(target)
      ) {
        setSignInOpen(false);
      }
    }
    if (signInOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [signInOpen]);

  useLayoutEffect(() => {
    const container = navContainerRef.current;
    const logo = navMeasureLogoRef.current;
    const links = navMeasureLinksRef.current;
    const actions = navMeasureActionsRef.current;

    if (!container || !logo || !links || !actions) {
      return;
    }

    const updateLayout = () => {
      const containerWidth = container.clientWidth;
      const logoWidth = logo.getBoundingClientRect().width;
      const linksWidth = links.getBoundingClientRect().width;
      const actionsWidth = actions.getBoundingClientRect().width;
      const safeGap = 72;
      const requiredWidth = logoWidth + linksWidth + actionsWidth + safeGap;
      setUseMobileLayout(containerWidth < requiredWidth);
    };

    updateLayout();

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);
    resizeObserver.observe(logo);
    resizeObserver.observe(links);
    resizeObserver.observe(actions);
    window.addEventListener("resize", updateLayout);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateLayout);
    };
  }, [loginEnabled]);

  useEffect(() => {
    if (!useMobileLayout) {
      setMobileMenuOpen(false);
    }
  }, [useMobileLayout]);

  const LogoComponent = logo === "agentica" ? AgenticaLogo : SymbolicaLogo;
  const logoHref = logo === "agentica" ? "/agentica" : "/";

  if (logoOnly) {
    return (
      <nav className="fixed inset-x-0 top-0 z-50 pointer-events-none">
        <div className="mx-auto flex max-w-7xl items-center px-6 py-5 sm:px-10">
          <Link
            href={logoHref}
            className="flex items-center pointer-events-auto"
          >
            <LogoComponent
              className={cn(
                "h-5 w-auto",
                isDark ? "text-white" : "text-[#0a0a0a]"
              )}
            />
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <>
      {/* Fixed Nav */}
      <nav
        className="fixed inset-x-0 top-0 z-50 backdrop-blur-md"
        style={{
          maskImage: "linear-gradient(to bottom, black 60%, transparent)",
          WebkitMaskImage: "linear-gradient(to bottom, black 60%, transparent)",
        }}
      >
        <div
          ref={navContainerRef}
          className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-10"
        >
          <Link href={logoHref} className="flex shrink-0 items-center">
            <LogoComponent
              className={cn(
                "h-5 w-auto",
                isDark ? "text-white" : "text-[#0a0a0a]"
              )}
            />
          </Link>

          {/* Desktop links */}
          <div
            className={cn(
              "mx-8 items-center gap-8 text-[13px] font-light",
              useMobileLayout ? "hidden" : "flex",
              isDark ? "text-white/50" : "text-black/60"
            )}
          >
            {NAV_LINKS_LEFT.map((link) => (
              <NavItem
                key={link.href}
                link={link}
                activePage={activePage}
                isDark={isDark}
              />
            ))}
            <div
              className={cn(
                "h-4 w-px",
                isDark ? "bg-white/20" : "bg-[#0a0a0a]/20"
              )}
            />
            {NAV_LINKS_RIGHT.map((link) => (
              <NavItem
                key={link.href}
                link={link}
                activePage={activePage}
                isDark={isDark}
              />
            ))}
          </div>

          {/* Desktop right: GitHub + Sign In */}
          <div
            className={cn(
              "shrink-0 items-center gap-3",
              useMobileLayout ? "hidden" : "flex"
            )}
          >
            <a
              href="https://github.com/symbolica-ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              <PillButton
                variant={isDark ? "secondary" : "outline"}
                icon="github"
                className="p-2"
              >
                <span className="sr-only">GitHub</span>
              </PillButton>
            </a>
            {loginEnabled && (
              <div ref={signInButtonRef}>
                <PillButton
                  type="button"
                  onClick={toggleSignIn}
                  variant={isDark ? "primary" : "outline"}
                  icon="none"
                >
                  Sign In
                </PillButton>
              </div>
            )}
          </div>

          {/* Hamburger — mobile only */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className={cn(
              "cursor-pointer items-center rounded-md p-2 transition-colors",
              useMobileLayout ? "inline-flex" : "hidden",
              isDark ? "hover:bg-white/10" : "hover:bg-black/10"
            )}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="site-mobile-navigation"
          >
            <Menu
              className={cn(
                "h-6 w-6",
                isDark ? "text-gray-300" : "text-gray-700"
              )}
            />
          </button>

          <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden opacity-0">
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-10">
              <div
                ref={navMeasureLogoRef}
                className="flex shrink-0 items-center"
              >
                <LogoComponent className="h-5 w-auto" />
              </div>
              <div
                ref={navMeasureLinksRef}
                className="mx-8 flex items-center gap-8 text-[13px] font-light whitespace-nowrap"
              >
                {NAV_LINKS_LEFT.map((link) => (
                  <span key={link.href}>{link.label}</span>
                ))}
                <div className="h-4 w-px" />
                {NAV_LINKS_RIGHT.map((link) => (
                  <span key={link.href}>{link.label}</span>
                ))}
              </div>
              <div
                ref={navMeasureActionsRef}
                className="flex shrink-0 items-center gap-3"
              >
                <PillButton
                  variant={isDark ? "secondary" : "outline"}
                  icon="github"
                  className="p-2"
                >
                  <span className="sr-only">GitHub</span>
                </PillButton>
                {loginEnabled && (
                  <PillButton
                    variant={isDark ? "primary" : "outline"}
                    icon="none"
                  >
                    Sign In
                  </PillButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Sign In dropdown — rendered outside nav to avoid mask clipping */}
      {loginEnabled && signInOpen && (
        <div
          ref={signInDropdownRef}
          className={cn(
            "fixed z-50 -translate-x-1/2 w-44 rounded-2xl border p-1.5 shadow-lg",
            isDark
              ? "border-[#0a0a0a]/10 bg-white"
              : "border-[#0a0a0a]/10 bg-[#FFFDF0]"
          )}
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          <Link
            href={`/login?redirect=${encodeURIComponent(env.NEXT_PUBLIC_AGENTICA_BASE_URL)}`}
            className="block rounded-xl px-5 py-2.5 text-center text-[13px] font-medium text-[#0a0a0a] border border-transparent hover:border-[#0a0a0a]/20 transition-colors"
            onClick={() => setSignInOpen(false)}
          >
            Agentica
          </Link>
          <Link
            href="/login"
            className="block rounded-xl px-5 py-2.5 text-center text-[13px] font-medium text-[#0a0a0a] border border-transparent hover:border-[#0a0a0a]/20 transition-colors"
            onClick={() => setSignInOpen(false)}
          >
            SDK Platform
          </Link>
        </div>
      )}

      {/* Mobile overlay */}
      <div
        className={cn(
          "fixed inset-0 z-[60] transition-opacity duration-200 ease-out",
          useMobileLayout ? "block" : "hidden",
          isDark ? "bg-black/60" : "bg-black/40",
          mobileMenuOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        )}
        onClick={closeMobileMenu}
      />

      {/* Mobile drawer */}
      <div
        id="site-mobile-navigation"
        aria-hidden={!mobileMenuOpen}
        className={cn(
          "fixed inset-y-0 right-0 z-[70] w-[18rem] transform transition-transform duration-300 ease-in-out",
          useMobileLayout ? "block" : "hidden",
          isDark
            ? "border-l border-white/10 bg-neutral-950"
            : "border-l border-black/10 bg-[#FFFDF0]",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="relative flex h-full flex-col px-6 py-8">
          <button
            type="button"
            onClick={closeMobileMenu}
            className={cn(
              "absolute right-4 top-4 cursor-pointer rounded-md p-2 transition-colors",
              isDark ? "hover:bg-white/10" : "hover:bg-black/10"
            )}
            aria-label="Close navigation menu"
          >
            <X
              className={cn(
                "h-6 w-6",
                isDark ? "text-gray-300" : "text-gray-700"
              )}
            />
          </button>
          <nav className="mt-10 flex-1">
            <ul className="space-y-2">
              {NAV_LINKS_LEFT.map((link) => (
                <MobileNavItem
                  key={link.href}
                  link={link}
                  activePage={activePage}
                  isDark={isDark}
                  onClose={closeMobileMenu}
                />
              ))}
              <li>
                <div
                  className={cn(
                    "my-2 h-px mx-3",
                    isDark ? "bg-white/10" : "bg-[#0a0a0a]/10"
                  )}
                />
              </li>
              {NAV_LINKS_RIGHT.map((link) => (
                <MobileNavItem
                  key={link.href}
                  link={link}
                  activePage={activePage}
                  isDark={isDark}
                  onClose={closeMobileMenu}
                />
              ))}
              <li className="pt-4">
                <a
                  href="https://github.com/symbolica-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                >
                  <PillButton
                    variant={isDark ? "secondary" : "outline"}
                    icon="github"
                    className="w-full justify-center"
                  >
                    GitHub
                  </PillButton>
                </a>
              </li>
              {loginEnabled && (
                <>
                  <li className="pt-2">
                    <Link
                      href={`/login?redirect=${encodeURIComponent(env.NEXT_PUBLIC_AGENTICA_BASE_URL)}`}
                      onClick={closeMobileMenu}
                    >
                      <PillButton
                        variant={isDark ? "primary" : "outline"}
                        icon="none"
                        className="w-full justify-center"
                      >
                        Agentica
                      </PillButton>
                    </Link>
                  </li>
                  <li className="pt-2">
                    <Link href="/login" onClick={closeMobileMenu}>
                      <PillButton
                        variant={isDark ? "primary" : "outline"}
                        icon="none"
                        className="w-full justify-center"
                      >
                        SDK Platform
                      </PillButton>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}
